// ResetPasswordPage.js — Define nova password com token válido
import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Loader2, KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password deve ter pelo menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("As passwords não coincidem");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("Password atualizada com sucesso");
      setTimeout(() => navigate("/login"), 2500);
    } catch (error) {
      const detail = error.response?.data?.detail || "Erro ao atualizar password";
      toast.error(typeof detail === "string" ? detail : "Erro");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4">
        <Card className="bg-white max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600">Link inválido</CardTitle>
            <CardDescription>O link de recuperação está incompleto ou foi adulterado.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/forgot-password">
              <Button className="w-full bg-brand-600 hover:bg-brand-700 text-white">Solicitar novo link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center text-sm text-slate-500 hover:text-brand-600 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar ao login
        </Link>
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-brand-600">Definir Nova Password</CardTitle>
            <CardDescription>Escolha uma password com pelo menos 6 caracteres.</CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <div className="space-y-3 text-center" data-testid="reset-done">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="text-sm">
                  Password atualizada com sucesso. A redirecionar para o login...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs">Nova Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Confirmar Password</Label>
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      A atualizar...
                    </>
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4 mr-2" />
                      Atualizar Password
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
